import React from 'react';
import { Appointment, calendarAppointments } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useQuery } from '@tanstack/react-query';
import { asyncWait } from '@uplift/utils';
import { useICSSubscriptionEventsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { camelcaseKeys } from '@uplift/types';


export const useMobileCalendarEventsList = (date: string, timezone: string) => {
  const me = useCurrentuser();
  const { data: icsData } = useICSSubscriptionEventsQuery(me.id);
  const currentDateIcsEvents = React.useMemo(() => {
    return icsData?.events.filter(e => e.fromDatetime.split('T')[0] === date) ?? [];
  }, [ date, icsData ]);
  const conflictsSet = React.useMemo(() => new Set(icsData?.conflicts.map(c => c.appointment.id) ?? []), [ icsData ]);
  const { data: appointmentsData, isLoading: isEventsLoading } = useQuery({
    queryKey: [ 'calendar', date, 'appointments' ] as const,
    queryFn: async () => {
      const dateTime = CalendarDateService.date.tz(date, DateFormats.ISO_DATE, timezone);
      const start = dateTime.startOf('day').utc().format();
      const end = dateTime.endOf('day').utc().format();
      const [ response ] = await Promise.all([
        calendarAppointments({
          datetime_from: start,
          datetime_to: end,
        }),
        asyncWait(300),
      ]);

      return response;
    }
  });

  const events = React.useMemo(() => {
    const appointmentEvents = appointmentsData?.appointments?.map(_appointment => {
      const appointment: Appointment = camelcaseKeys(_appointment);
      const patient = appointmentsData.patients?.[appointment.patientId];
      const event = {
        type: 'appointment' as const,
        start: CalendarDateService.date(appointment.fromDatetime),
        end: CalendarDateService.date(appointment.toDatetime),
        appointment: appointment,
        patient: patient,
        isICSConflict: conflictsSet.has(appointment.id) ?? false,
        elevation: 0,
      };

      return event;
    }) ?? [];

    const icsEvents = currentDateIcsEvents.map(e => ({
      type: 'ics' as const,
      start: CalendarDateService.date(e.fromDatetime),
      end: CalendarDateService.date(e.toDatetime),
      event: e,
      elevation: 0,
    })) ?? [];

    const allDayICSEvents = icsEvents.filter(e => e.event.allDay);
    const duringDayICSEvents = icsEvents.filter(e => !e.event.allDay);
    const duringDayEvents = [ ...appointmentEvents, ...duringDayICSEvents ].sort((a, b) => CalendarDateService.compare(a.start, b.start));

    const resultEvents = [ ...allDayICSEvents, ...duringDayEvents ];
    const resultEventsWithSpacing = resultEvents.reduce((prev, curr) => {
      const prevEl = prev[prev.length - 1];

      if (!prevEl) {
        return [ ...prev, curr ];
      }

      if (prevEl.type === 'ics' && prevEl.event.allDay) {
        return [ ...prev, curr ];
      }

      const overflowEl = [ ...prev ].reverse().find(p => {
        if (p.type === 'ics' && p.event.allDay) {
          return false;
        }

        return p.end.isAfter(curr.start);
      });

      return [
        ...prev,
        {
          ...curr,
          elevation: overflowEl ? overflowEl.elevation + 1 : 0
        }
      ];
    }, [] as typeof resultEvents);

    return resultEventsWithSpacing;
  }, [ appointmentsData, currentDateIcsEvents, conflictsSet ]);

  return { events, isEventsLoading };
};
